import React, { useState, useEffect } from 'react';
import GiftItem from '../GiftItem/GiftItem';
import styles from './GiftPopup.module.scss';
import { useSharedCart } from '../../entrypoints/theme';
import Lock from '../Icons/Lock';
import { cartStore } from '../../hooks/cartStore';

interface Product {
  id: number;
}

interface CartState {
  gift_threshold_1?: string;
  gift_threshold_2?: string;
  gift_threshold_3?: string;
  main_items_total?: string;
}

export default function GiftPopup(): React.ReactElement {
  const { cartState } = useSharedCart() as { cartState: CartState };
  const [isOpen, setIsOpen] = useState<boolean>(cartStore.isGiftPopupOpen);
  const [isAddingGift, setIsAddingGift] = useState<boolean>(false);
  const tierOneCollectionHandle: string = "tier-1-collection";
  const tierTwoCollectionHandle: string = "tier-2-collection";
  const tierThreeCollectionHandle: string = "tier-3-collection";

  const [tierOneProducts, setTierOneProducts] = useState<Product[]>([]);
  const [tierTwoProducts, setTierTwoProducts] = useState<Product[]>([]);
  const [tierThreeProducts, setTierThreeProducts] = useState<Product[]>([]);
  
  const tierOneMin: number = parseFloat(cartState?.gift_threshold_1 || '0');
  const tierTwoMin: number = parseFloat(cartState?.gift_threshold_2 || '0');
  const tierThreeMin: number = parseFloat(cartState?.gift_threshold_3 || '0');

  const totalPriceDollars: number = parseFloat(cartState?.main_items_total ?? '0');

  useEffect(() => {
    const fetchProducts = async (handle: string): Promise<Product[]> => {
      const response = await fetch(`/collections/${handle}/products.json`);
      const data = await response.json();
      return data.products || [];
    };

    const fetchAllTiers = async () => {
      try {
        const [tierOne, tierTwo, tierThree] = await Promise.all([
          fetchProducts(tierOneCollectionHandle),
          fetchProducts(tierTwoCollectionHandle),
          fetchProducts(tierThreeCollectionHandle),
        ]);

        setTierOneProducts(tierOne);
        setTierTwoProducts(tierTwo);
        setTierThreeProducts(tierThree);
      } catch (error) {
        console.error("Error fetching tier products:", error);
      }
    };

    fetchAllTiers();
  }, [tierOneCollectionHandle, tierTwoCollectionHandle, tierThreeCollectionHandle]);

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };

  // Determine which tiers are unlocked (only if threshold > 0)
  const isTierOneUnlocked = tierOneMin > 0 && totalPriceDollars >= tierOneMin;
  const isTierTwoUnlocked = tierTwoMin > 0 && totalPriceDollars >= tierTwoMin;
  const isTierThreeUnlocked = tierThreeMin > 0 && totalPriceDollars >= tierThreeMin;

  // Combine products from all unlocked tiers
  const allUnlockedProducts = [
    ...(isTierOneUnlocked ? tierOneProducts : []),
    ...(isTierTwoUnlocked ? tierTwoProducts : []),
    ...(isTierThreeUnlocked ? tierThreeProducts : []),
  ];

  // Helper function to render a locked tier section
  // Only call this for tiers that are *not* unlocked and have products/threshold
  const renderLockedTier = (tierProducts: Product[], tierMin: number, tierName: string): JSX.Element => {
    const amountNeeded: number = tierMin - totalPriceDollars;

    return (
      <div className={styles.tierSection}>
        <div className={styles.unlockMessage}>
          <Lock />
          <span>
            Spend {formatCurrency(amountNeeded)} more to unlock {tierName} gifts
          </span>
        </div>
        <div className={`${styles.giftGrid} ${styles.lockedGrid}`}>
          {tierProducts.map((product) => (
            <GiftItem key={product.id} product={product} isLocked={true} isAddingGift={isAddingGift} />
          ))}
        </div>
      </div>
    );
  };

  // Check if any gifts exist at all
  const anyGiftsAvailable = tierOneProducts.length > 0 || tierTwoProducts.length > 0 || tierThreeProducts.length > 0;

  return (
    isOpen && (
      <>
        <div className={styles.modalOverlay} onClick={cartStore.closeGiftPopup}>
          <div
            className={styles.giftPopupContainer}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className={styles.closeButton}
              onClick={cartStore.closeGiftPopup}
            >
              &times;
            </button>

            <div className={styles.popupHeader}>
              <h2 className={styles.popupTitle}>
                Choose Your Free Gift - Limited Time Only
              </h2>
              <p className={styles.popupSubtitle}>
                Choose your free gift - supplies are limited and the offer ends
                soon.
              </p>
            </div>
            {anyGiftsAvailable ? (
              <>
                {allUnlockedProducts.length > 0 && (
                  <div className={styles.tierSection}>
                    <div className={styles.giftGrid}>
                      {allUnlockedProducts.map((product) => (
                        <GiftItem
                          key={product.id}
                          product={product}
                          isLocked={false}
                          isAddingGift={isAddingGift}
                          setIsAddingGift={setIsAddingGift}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {!isTierOneUnlocked &&
                  tierOneProducts.length > 0 &&
                  tierOneMin > 0 &&
                  renderLockedTier(tierOneProducts, tierOneMin, "Tier 1")}
                {!isTierTwoUnlocked &&
                  tierTwoProducts.length > 0 &&
                  tierTwoMin > 0 &&
                  renderLockedTier(tierTwoProducts, tierTwoMin, "Tier 2")}
                {!isTierThreeUnlocked &&
                  tierThreeProducts.length > 0 &&
                  tierThreeMin > 0 &&
                  renderLockedTier(tierThreeProducts, tierThreeMin, "Tier 3")}
              </>
            ) : (
              <p>Loading gifts...</p>
            )}
          </div>
        </div>
      </>
    )
  );
}

GiftPopup.displayName = 'GiftPopup';
